import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { FiRepeat } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  DetailTabBar, DetailRow, DetailStatusPill, ApproverCard, ImageBlock, FeedbackTab,
} from "../../components/financeDetail";
import { getSalesContractByIdApi } from "../../api/finance.salesContract.api";
import { formatMoney } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#08142D";
const MUTED = "#94A3B8";
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const SalesContractDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getSalesContractByIdApi(id).then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  const tabs = useMemo(() => {
    const r = [{ key: "overview", title: "Tổng quan" }, { key: "transaction", title: "Giao dịch" }];
    if (data?._id) r.push({ key: "interactNote", title: "Phản hồi" });
    return r;
  }, [data]);
  useEffect(() => { if (tab >= tabs.length) setTab(0); }, [tabs.length, tab]);
  const activeKey = tabs[tab]?.key || "overview";

  const st = data?.status;
  const isApproved = st === "APPROVED";
  const apCode = data?.locking?.apartment?.code || data?.ttkq?.locking?.apartment?.code || "";
  const apName = data?.locking?.apartment?.name || data?.ttkq?.locking?.apartment?.name || "";

  const outlineBtn = { variant: "outline", borderColor: "#DCE1EA", color: TEXT, borderRadius: "8px", h: "48px", _hover: { bg: "#F7F9FC" } };

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : !data ? (
        <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin HDMB</Text></Flex>
      ) : (
        <>
          <DetailTabBar tabs={tabs} index={tab} onChange={setTab} />

          <Box flex="1" overflowY="auto" px="16px" pt="16px">
            {activeKey === "overview" && (
              <Box pb="20px">
                <Text fontSize="16px" fontWeight={600} color={TEXT} noOfLines={2}>
                  {data?.project?.name || ""}{data?.project?.name ? " - " : ""}{apCode || "Không định danh"}
                </Text>
                <Box mt="12px"><DetailStatusPill status={st} /></Box>
                <ApproverCard approver={data?.approver} dateLabel={fmtD(data?.createdAt)} />

                <Box mt="8px">
                  <DetailRow label="Dự án" value={data?.project?.name} onPress={data?.projectId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${data.projectId}`) : undefined} />
                  {data?.code && <DetailRow label="Mã HDMB" value={data.code} />}
                  <DetailRow label="Căn hộ" value={apCode ? `${apCode} (${apName})` : "-"} />
                  <DetailRow label="Khách hàng" value={data?.customer?.name} onPress={data?.customerId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${data.customerId}`) : undefined} />
                  {!data?.ttkqId ? (
                    <DetailRow label="Trạng thái locking" value="Xem chi tiết locking" onPress={data?.lockingId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/locking/${data.lockingId}`) : undefined} />
                  ) : (
                    <DetailRow label="Trạng thái TTKQ" value="Xem chi tiết TTKQ" onPress={() => navigate(`${EMP_BASE_PATH}/tai-chinh/ttkq/${data.ttkqId}`)} />
                  )}
                  <DetailRow label="Phương thức thanh toán" value={data?.paymentMethod?.name} />
                  <DetailRow label="Ngày ký HDMB" value={fmtD(data?.time)} />
                  <DetailRow label="Ngày đóng tiền HDMB" value={fmtD(data?.priceTime)} />
                  <DetailRow label="Ngày tạo HDMB" value={fmtD(data?.createdAt)} />
                  <DetailRow label="Số tiền đóng HDMB" value={formatMoney(data?.price || 0)} color={APP.primary200} />
                  <DetailRow label="Ghi chú" value={data?.note} last />
                </Box>
              </Box>
            )}

            {activeKey === "transaction" && (
              <Box pb="20px">
                <ImageBlock images={data?.delegationImage} emptyText="Chưa có ảnh UNC" />
                <Box h="16px" />
                <ImageBlock images={data?.priceSheetImage} emptyText="Chưa có ảnh phiếu tính giá" />
                <Box h="16px" />
                <ImageBlock images={data?.salesContractImages} emptyText="Chưa có ảnh HDMB" />
              </Box>
            )}

            {activeKey === "interactNote" && (
              <Box pb="20px"><FeedbackTab query={{ lockingId: id }} /></Box>
            )}
          </Box>

          {tab === 0 && isApproved && (
            <Box borderTop="1px solid #EEF1F6" p="16px">
              <Button w="100%" {...outlineBtn} leftIcon={<FiRepeat />} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban/chuyen/${id}`)}>Chuyển HDMB</Button>
            </Box>
          )}
        </>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết đơn đăng ký HDMB" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Chi tiết đơn đăng ký HDMB" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/hop-dong-mua-ban`)} />
      {pageBody}
    </Box>
  );
};

export default SalesContractDetail;
